import type { IncomeSource, PlanAssumptions } from '@/api/generated/model';
import type { FinancialInfo } from '@/services/goalPlanningStorage';

export interface IncomeSourceYearAmount {
  id: string;
  label: string;
  growthRate: number;
  amount: number;
}

export function resolveIncomeSources(assumptions: PlanAssumptions): IncomeSource[] {
  return assumptions.incomeSources ?? [];
}

export function annualIncomeForSource(source: IncomeSource, yearIndex: number): number {
  const base = source.frequency === 'monthly' ? source.amount * 12 : source.amount;
  return base * Math.pow(1 + source.growthRate, yearIndex);
}

export function sumAnnualizedIncomeSources(sources: IncomeSource[]): number {
  return sources.reduce((sum, source) => {
    const base = source.frequency === 'monthly' ? source.amount * 12 : source.amount;
    return sum + base;
  }, 0);
}

export function sumIncomeSourcesForYear(
  sources: IncomeSource[],
  yearIndex: number
): { total: number; bySource: IncomeSourceYearAmount[] } {
  const bySource = sources.map((source) => ({
    id: source.id,
    label: source.label,
    growthRate: source.growthRate,
    amount: annualIncomeForSource(source, yearIndex),
  }));
  const total = bySource.reduce((sum, row) => sum + row.amount, 0);
  return { total, bySource };
}

export function financialInfoToIncomeSources(
  financialInfo: FinancialInfo,
  growthRate = 0.03
): IncomeSource[] {
  const sources: IncomeSource[] = [];

  if (financialInfo.salary > 0) {
    sources.push({
      id: crypto.randomUUID(),
      label: 'Salary',
      sourceKind: 'employment',
      amount: financialInfo.salary,
      frequency: 'annual',
      growthRate,
    });
  }

  for (const stream of financialInfo.revenueStreams) {
    if (!stream.name.trim() && stream.amount <= 0) continue;
    sources.push({
      id: crypto.randomUUID(),
      label: stream.name.trim() || 'Revenue stream',
      sourceKind: 'business',
      amount: stream.amount,
      frequency: stream.frequency === 'yearly' ? 'annual' : 'monthly',
      growthRate,
    });
  }

  return sources;
}

export function assumptionsFromFinancialInfo(
  financialInfo: FinancialInfo,
  growthRate = 0.03
): Pick<
  PlanAssumptions,
  'currentW2Income' | 'incomeAnnualGrowthRate' | 'currency' | 'incomeSources'
> {
  const incomeSources = financialInfoToIncomeSources(financialInfo, growthRate);
  return {
    currentW2Income: sumAnnualizedIncomeSources(incomeSources),
    incomeAnnualGrowthRate: growthRate,
    currency: financialInfo.currency,
    incomeSources,
  };
}
